// Thin fetch wrapper for the Spring Boot backend.
// Base URL points at the local backend by default; override with
// REACT_APP_API_BASE_URL in a .env file for other environments.
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const TOKEN_KEY = "trucktax_token";
const USER_KEY = "user";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// Mirrors the logged-in user (including taxPreparer) into sessionStorage so
// it survives a hard refresh — AppContext state is in-memory only and resets
// on reload, sessionStorage doesn't.
export function getStoredUser() {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Fired whenever the stored user is set or cleared, so SessionManager can
// (re)arm or stop its timers on sign-in/sign-out - sign-in is an in-app
// navigation, not a page load, so its mount-time check alone never sees it.
export const SESSION_CHANGE_EVENT = "session-change";

export function setStoredUser(user) {
  if (user) {
    sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    window.dispatchEvent(new Event(SESSION_CHANGE_EVENT));
  }
}

export function clearStoredUser() {
  sessionStorage.removeItem(USER_KEY);
  window.dispatchEvent(new Event(SESSION_CHANGE_EVENT));
}

// login()/authService.js merges the login response's expiresAt (epoch ms)
// onto the stored user, so SessionManager can schedule a hard logout exactly
// when the token expires without guessing the server's TTL.
export function getMsUntilExpiry() {
  const user = getStoredUser();
  if (!user || !user.expiresAt) return null;
  return Math.max(0, user.expiresAt - Date.now());
}

// Normalizes the backend's error envelope:
// { error: { code, message, errors: [{ field, message, ... }] } }
// into a single, displayable message.
function extractErrorMessage(body) {
  if (!body) return "Something went wrong. Please try again.";
  const err = body.error;
  if (!err) return "Something went wrong. Please try again.";
  if (Array.isArray(err.errors) && err.errors.length > 0) {
    return err.errors.map((e) => e.message).filter(Boolean).join(" ");
  }
  return err.message || "Something went wrong. Please try again.";
}

// A 401 on a request that carried a token means the JWT is expired/invalid:
// end the session and send the user to sign in (Login shows "Your session
// expired" for ?expired=1), instead of letting each screen render its own
// generic error. Unauthenticated calls (login, register, reset) are excluded
// by the caller's check, so a wrong-password 401 still shows on the form.
let sessionExpiredRedirecting = false;
function handleSessionExpired() {
  if (sessionExpiredRedirecting) return;
  sessionExpiredRedirecting = true;
  clearToken();
  clearStoredUser();
  window.location.replace("/login?expired=1");
}

export async function apiRequest(path, { method = "GET", body, auth = true, headers = {} } = {}) {
  const finalHeaders = { "Content-Type": "application/json", ...headers };

  const token = auth ? getToken() : null;
  if (token) finalHeaders.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: finalHeaders,
    body: body ? JSON.stringify(body) : undefined,
  });

  let data = null;
  const text = await response.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    if (response.status === 401 && token) {
      handleSessionExpired();
    }
    const message = extractErrorMessage(data);
    const error = new Error(message);
    error.status = response.status;
    error.body = data;
    throw error;
  }

  return data;
}

// Extracts the filename from a Content-Disposition: attachment; filename="..." header.
function extractFilename(response, fallback) {
  const header = response.headers.get("Content-Disposition") || "";
  const match = header.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match ? decodeURIComponent(match[1]) : fallback;
}

// Like apiRequest, but for binary responses (PDFs) instead of JSON.
// -> { blob, filename }. Errors still come back as the backend's JSON error
// envelope when possible.
export async function apiRequestBlob(path, { method = "GET", auth = true, headers = {}, fallbackFilename } = {}) {
  const finalHeaders = { ...headers };

  const token = auth ? getToken() : null;
  if (token) finalHeaders.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_BASE_URL}${path}`, { method, headers: finalHeaders });

  if (!response.ok) {
    if (response.status === 401 && token) {
      handleSessionExpired();
    }
    let data = null;
    try {
      data = JSON.parse(await response.text());
    } catch {
      data = null;
    }
    const message = extractErrorMessage(data);
    const error = new Error(message);
    error.status = response.status;
    error.body = data;
    throw error;
  }

  const blob = await response.blob();
  return { blob, filename: extractFilename(response, fallbackFilename) };
}
